import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";

import api from "../api/axios";
import { VAULT_CLAIM_BADGE_STORAGE_KEY } from "../utils/vaultAttention";
import { runBackgroundRequest } from "../utils/backgroundRequestQueue";

const BACKGROUND_REFRESH_DEBOUNCE_MS = 400;
const BACKGROUND_REFRESH_COOLDOWN_MS = 1_500;

const readStoredVaultClaimable = () => {
  if (typeof window === "undefined") return false;
  return window.sessionStorage.getItem(VAULT_CLAIM_BADGE_STORAGE_KEY) === "1";
};

const EMPTY_CONTEXT = {
  hasFullVaultCoins: false,
  refresh: async () => {},
};

export const AppStateContext = createContext(null);

export const AppStateProvider = ({
  children,
  enabled = true,
  intervalMs = 30_000,
  userId = null,
}) => {
  const [optimisticVaultClaimable, setOptimisticVaultClaimable] = useState(
    readStoredVaultClaimable
  );
  const lastRequestStartedAtRef = useRef(0);
  const requestGenerationRef = useRef(0);
  const claimableEventGenerationRef = useRef(-1);
  const backgroundTimerRef = useRef(null);
  const refreshPromiseRef = useRef(null);

  const { data, refetch } = useQuery({
    queryKey: ["app_state_summary", userId],
    enabled: Boolean(enabled && userId),
    queryFn: async () => {
      const requestGeneration = requestGenerationRef.current + 1;
      requestGenerationRef.current = requestGeneration;
      lastRequestStartedAtRef.current = Date.now();
      const response = await runBackgroundRequest(() => (
        api.get("/app-state/summary/", {
          meta: { suppressGlobalError: true },
        })
      ));
      return {
        payload: response?.data || {},
        requestGeneration,
      };
    },
    refetchInterval: intervalMs,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  const refresh = useCallback(() => {
    if (!enabled || !userId) return Promise.resolve();
    if (refreshPromiseRef.current) return refreshPromiseRef.current;

    const promise = refetch().finally(() => {
      if (refreshPromiseRef.current === promise) {
        refreshPromiseRef.current = null;
      }
    });
    refreshPromiseRef.current = promise;
    return promise;
  }, [enabled, refetch, userId]);

  const scheduleBackgroundRefresh = useCallback(() => {
    if (!enabled || !userId || typeof window === "undefined") return;
    window.clearTimeout(backgroundTimerRef.current);
    const cooldownRemaining = Math.max(
      0,
      BACKGROUND_REFRESH_COOLDOWN_MS -
        (Date.now() - lastRequestStartedAtRef.current)
    );
    backgroundTimerRef.current = window.setTimeout(
      () => void refresh(),
      Math.max(BACKGROUND_REFRESH_DEBOUNCE_MS, cooldownRemaining)
    );
  }, [enabled, refresh, userId]);

  useEffect(() => {
    if (
      Number(data?.requestGeneration || 0) <=
      claimableEventGenerationRef.current
    ) {
      return;
    }
    const claimable = data?.payload?.vault?.claimable;
    if (typeof claimable !== "boolean") return;
    setOptimisticVaultClaimable(claimable);
  }, [data?.payload?.vault?.claimable, data?.requestGeneration]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (optimisticVaultClaimable) {
      window.sessionStorage.setItem(VAULT_CLAIM_BADGE_STORAGE_KEY, "1");
    } else {
      window.sessionStorage.removeItem(VAULT_CLAIM_BADGE_STORAGE_KEY);
    }
    return undefined;
  }, [optimisticVaultClaimable]);

  useEffect(() => {
    if (!enabled || !userId || typeof window === "undefined") return undefined;

    const handleClaimableState = (event) => {
      claimableEventGenerationRef.current = requestGenerationRef.current;
      setOptimisticVaultClaimable(Boolean(event?.detail?.claimable));
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") scheduleBackgroundRefresh();
    };
    const handleProductMutation = () => {
      void refresh();
    };

    window.addEventListener("vg-vault-claimable-state", handleClaimableState);
    window.addEventListener("vg-vault-coins-collected", handleProductMutation);
    window.addEventListener("vg-vault-coins-spent", handleProductMutation);
    window.addEventListener("focus", scheduleBackgroundRefresh);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearTimeout(backgroundTimerRef.current);
      window.removeEventListener("vg-vault-claimable-state", handleClaimableState);
      window.removeEventListener("vg-vault-coins-collected", handleProductMutation);
      window.removeEventListener("vg-vault-coins-spent", handleProductMutation);
      window.removeEventListener("focus", scheduleBackgroundRefresh);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, refresh, scheduleBackgroundRefresh, userId]);

  const value = useMemo(
    () => ({
      hasFullVaultCoins: optimisticVaultClaimable,
      refresh,
    }),
    [optimisticVaultClaimable, refresh]
  );

  return (
    <AppStateContext.Provider value={value}>
      {children}
    </AppStateContext.Provider>
  );
};

export const useAppState = () => useContext(AppStateContext) || EMPTY_CONTEXT;
